'use client'

import Image from 'next/image'
import Link from 'next/link'
import { Group, Stack, Text } from '@mantine/core'
import { IconMapPin } from '@tabler/icons-react'
import { hyphenatePharmacyName } from '@/lib/store/api/directoryPharmacy/mapDirectoryPharmacyResponse'
import { PharmacyDetailSkuContext, pharmacyRoutes } from '@/lib/routes/directory_pharmacy'
import { humanizePrice } from '@/lib/utilities'
import { PharmacyComparisonResult } from '@/types/interfaces/pharmacyComparison'
import { GENERIC_PHARMACY_ICON_SRC, getListRowIcon } from './pharmacyChainIcon'
import classes from './PharmacyComparisonRow.module.css'

interface PharmacyComparisonRowProps {
    pharmacy: PharmacyComparisonResult
    /** The medication PDP this row is shown on, if any — carried into the pharmacy
     *  detail link so it can offer a floating "Request medication" button for this SKU. */
    skuContext?: PharmacyDetailSkuContext
}

export function PharmacyComparisonRow({ pharmacy, skuContext }: PharmacyComparisonRowProps) {
    // Keyed off fulfillment_owner (the pricing-group machine name), not chain_name —
    // chain_name is increasingly Google Places-sourced (e.g. "CVS Pharmacy #1234") and
    // doesn't normalize cleanly to the bundled icon set. See the fulfillment_owner
    // field doc in types/interfaces/pharmacyComparison.ts.
    const icon = getListRowIcon(pharmacy.fulfillment_owner ?? '')
    const pharmacyName = hyphenatePharmacyName(`${pharmacy.chain_name} Pharmacy`)
    const detailHref = pharmacy.friendly_id == null
        ? null
        : skuContext
            ? pharmacyRoutes.detailFromMedicationPdp(pharmacyName, pharmacy.friendly_id, skuContext)
            : pharmacyRoutes.detail(pharmacyName, pharmacy.friendly_id)

    const sublabel = [
        pharmacy.distance_miles != null ? `${pharmacy.distance_miles} mi` : null,
        pharmacy.address_line1,
    ]
        .filter(Boolean)
        .join(' · ')

    const content = (
        <Group gap={20} align="center" wrap="nowrap" className={classes.row}>
            <div className={classes.logoBadge}>
                {icon ? (
                    icon.isWordmark ? (
                        <Image
                            src={icon.src}
                            alt={pharmacy.chain_name}
                            width={44}
                            height={24}
                            className={classes.wordmark}
                        />
                    ) : (
                        <Image src={icon.src} alt={pharmacy.chain_name} width={72} height={72} />
                    )
                ) : (
                    <Image src={GENERIC_PHARMACY_ICON_SRC} alt="" width={20} height={20} />
                )}
            </div>
            <Group justify="space-between" align="flex-start" wrap="nowrap" className={classes.details}>
                <Stack gap={2} className={classes.name}>
                    <Text fz={16} fw="var(--gm-weight-text-bold, 600)" c="var(--gm-foreground-fg-primary)">
                        {pharmacy.chain_name}
                    </Text>
                    {sublabel && (
                        <Group gap={8} align="center" wrap="nowrap">
                            <IconMapPin size={16} color="var(--gm-foreground-fg-secondary)" />
                            <Text fz={14} fw="var(--gm-weight-text, 450)" c="var(--gm-foreground-fg-secondary)">
                                {sublabel}
                            </Text>
                        </Group>
                    )}
                </Stack>
                <Text
                    fz={16}
                    fw="var(--gm-weight-text-bold, 600)"
                    c="var(--gm-foreground-fg-primary)"
                    className={classes.price}
                >
                    {pharmacy.total_price_cents != null
                        ? humanizePrice(pharmacy.total_price_cents)
                        : 'Priced after visit'}
                </Text>
            </Group>
        </Group>
    )

    if (!detailHref) {
        return content
    }

    return (
        <Link href={detailHref} className={classes.rowLink}>
            {content}
        </Link>
    )
}
